'use client'

import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useRef } from 'react'

import { useAuth } from '@/contexts/AuthContext'
import { useNewsModal } from '@/contexts/NewsModalContext'
import { fetchWithAuth } from '@/utils/api-error-handler'

/** Obfuscated param set by login/signup redirect; when present we fetch show-news and maybe open modal */
const FROM_LOGIN_PARAM = 'fl'

/**
 * When URL has fl=1 (set by login/signup redirect): fetches GET /v1/user/show-news.
 * If API says show_news is true (or request fails), opens the news modal. Then strips the param from the URL.
 */
export function NewsModalGate() {
  const { user, isLoading: authLoading } = useAuth()
  const { openModal } = useNewsModal()
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const router = useRouter()
  const didRun = useRef(false)

  const tryShowNewsFromUrl = useCallback(async () => {
    const hasParam = searchParams.get(FROM_LOGIN_PARAM) === '1'
    if (!hasParam || !user) return

    const API_URL = process.env.NEXT_PUBLIC_API_URL?.replace(/\/$/, '')
    let shouldShow = false

    try {
      if (API_URL) {
        const res = await fetchWithAuth(
          `${API_URL}/v1/user/show-news`,
          {
            method: 'GET',
            credentials: 'include',
            headers: { Accept: 'application/json' },
          },
          undefined,
          { skipUnauthorizedRedirect: true }
        )
        if (res.ok) {
          const json = (await res.json()) as { show_news?: boolean }
          shouldShow = json.show_news === true
        } else {
          shouldShow = true
        }
      } else {
        shouldShow = true
      }
    } catch {
      shouldShow = true
    }

    // Strip param from URL so refresh doesn't trigger again
    const next = new URLSearchParams(searchParams)
    next.delete(FROM_LOGIN_PARAM)
    const qs = next.toString()
    const replaceUrl = qs ? `${pathname}?${qs}` : pathname
    router.replace(replaceUrl, { scroll: false })

    if (shouldShow) {
      openModal()
    }
  }, [user, openModal, searchParams, pathname, router])

  useEffect(() => {
    if (authLoading || !user || didRun.current) return
    if (searchParams.get(FROM_LOGIN_PARAM) !== '1') return
    didRun.current = true
    const t = setTimeout(() => {
      void tryShowNewsFromUrl()
    }, 300)
    return () => clearTimeout(t)
  }, [authLoading, user, searchParams, tryShowNewsFromUrl])

  return null
}
